import type {
  AccountingExportCapabilities, AccountingMapping, AccountRef, AddGroupMembersInput, BreakdownLine, ChartAccount, ChartAccountType, ChartImportResult, CodeBulkAction,
  CodeBulkApplyInput, CodeBulkPreview, CodeBulkPreviewItem, CodeBulkRequest, CodeBulkResult, CodeKind, CodeMappingStatus, CodeReadiness, CodeReadinessItem,
  CodeReadinessParams, CreateChartAccountInput, CreateEmployeeGroupInput, CreateExportInput, CreateExportResult, CreateMappingInput, DefaultAccountRef, EligiblePayroll,
  EligiblePayrollParams, EligiblePayrolls, EmployeeGroup, EmployeeGroupMember, EmployerContributions, ExistingExportRef, ExportAuditItem, ExportDetail,
  ExportMessage, ExportOptions, ExportOptionsInput, ExportPreview, ExportSettings, ExportStateChange, ExportStatus, ExportSummary, ExcludedItem, JournalLine, Journal,
  ListChartAccountsParams, ListExportsParams, ListMappingsParams, MappingReadiness, NetPayDetailLine, NetPayDetailMode, OptionSource, Paged, PreviewConfiguration,
  PreviewEmployee, PreviewParams, PreviewSummary, ReconciliationFigures, ReplacementInput, SaveExportSettingsInput, SourceCoverageItem, SourceLine, UnmappedItem,
  UnusedWageMappings, UpdateEmployeeGroupInput, WageGroupDimension, WageGroupGrid, WageGroupLine, WageGroupParams, WageGroupRow, WageGroupStatus, WageMappingMode,
} from './exportTypes';
import { WAGE_GROUP_DIMENSIONS, categorySide } from './exportTypes';
import { toCents } from './money';

/**
 * The seam between the Accounting API's wire shapes (contract section 15; pinned by __fixtures__ once the backend writes them) and the
 * UI types. Two things happen here and nowhere else:
 *   1. every money member (a JSON number with two decimals) becomes INTEGER CENTS, read through its decimal text, never by arithmetic on the double;
 *   2. a member the server did not send becomes null / an empty list, never a guess.
 * Request bodies and query strings are built here too, so the Http gateway tests pin them in one place.
 */

// ---- raw wire shapes ----

export interface RawEligiblePayroll {
  payrollRunId: string; payScheduleId?: string | null; payScheduleLabel?: string | null; periodStart: string; periodEnd: string; checkDate: string;
  employeeCount: number; grossEarnings: number; netPay: number; exportStatus?: string | null;
}
export interface RawEligiblePayrolls {
  items?: RawEligiblePayroll[] | null; payScheduleOptions?: { id?: string | null; label: string }[] | null;
}
export interface RawPreviewEmployee {
  employeeId: string; employeeReference?: string | null; displayName?: string | null; department?: string | null; location?: string | null;
  grossEarnings: number; netPay: number;
}
export interface RawJournalLine {
  lineNumber: number; side: string; amount: number; accountNumber: string; accountName: string; accountType: string; category: string;
  subcategory?: string | null; payrollCode?: string | null; employeeReference?: string | null; department?: string | null; location?: string | null;
  classDimension?: string | null; checkReference?: string | null; description: string; mappingId: string; mappingVersion: number; sourceLineIds?: string[] | null;
  groupDimension?: string | null; groupKey?: string | null; groupLabel?: string | null; resolutionLevel?: string | null; resolutionReason?: string | null;
}
export interface RawOptions {
  netPayDetailMode?: string | null; wageMappingMode?: string | null; wageGroupDimension?: string | null; source?: string | null; recorded?: boolean | null;
  settingsVersion?: number | null; groupConfigVersion?: number | null;
}
/** An account as the server may name it: `{ accountNumber, accountName }` (or `{ number, name }`), or just text. */
export type RawAccountRef = { accountNumber?: string | null; accountName?: string | null; number?: string | null; name?: string | null } | string;
export interface RawBreakdown {
  key?: string | null; code?: string | null; taxTypeCode?: string | null; taxType?: string | null; category?: string | null; label?: string | null; name?: string | null;
  amount?: number | null; total?: number | null; account?: RawAccountRef | null; note?: string | null;
  /** The real API's tax rows (`kind`, `subcategory`, `payrollCode`), deduction rows (`accountNumber`, `accountName`) and employer-contribution rows (`status`). */
  kind?: string | null; subcategory?: string | null; payrollCode?: string | null; accountNumber?: string | null; accountName?: string | null; status?: string | null;
}
export interface RawWageGroup {
  groupKey: string; label?: string | null; employeeCount?: number | null; grossAmount?: number | null; account?: RawAccountRef | null; mappingId?: string | null;
  mappingVersion?: number | null; reason?: string | null; status?: string | null;
}
export interface RawJournal {
  reference: string; totalDebits: number; totalCredits: number; difference: number; isBalanced: boolean; lines?: RawJournalLine[] | null;
}
export interface RawReconciliation {
  finalizedGross: number; employeeTaxes: number; employerTaxes: number; preTaxDeductions: number; postTaxDeductions: number; garnishments: number;
  employerContributions?: number | null; netPay: number; totalPayrollCost: number; journalDebits: number; journalCredits: number; unmappedAmount: number;
  excludedAmount: number; difference: number; isComplete: boolean;
}
export interface RawSourceLine {
  id: string; calculationId: string; employeeReference?: string | null; category: string; subcategory?: string | null; payrollCode?: string | null;
  jurisdiction?: string | null; side: string; amount: number; provenance: string; posted: boolean;
}
export interface RawUnmapped {
  category: string; subcategory?: string | null; payrollCode?: string | null; lineCount: number; amount: number; reason: string;
}
export interface RawExcluded { category: string; amount: number; reason: string; mappingId: string }
export interface RawPreview {
  payrollRunId: string; mode: string; accountingDate: string; status: string; currency?: string | null; employees?: RawPreviewEmployee[] | null;
  journal?: RawJournal | null; reconciliation?: RawReconciliation | null; sourceLines?: RawSourceLine[] | null; unmapped?: RawUnmapped[] | null;
  excluded?: RawExcluded[] | null; warnings?: ExportMessage[] | null; blockers?: ExportMessage[] | null; sourceCoverage?: SourceCoverageItem[] | null;
  existingExport?: { id: string; status: string } | null;
  options?: RawOptions | null;
  configuration?: (RawOptions & { options?: RawOptions | null; mappingVersionMax?: number | null; exportKeyVersion?: number | null }) | null;
  summary?: {
    employeeCount?: number | null; groupCount?: number | null; earningCodeCount?: number | null; deductionCodeCount?: number | null; mappedComponents?: number | null;
    missingMappings?: number | null; ambiguousMappings?: number | null; totalDebits?: number | null; totalCredits?: number | null; variance?: number | null;
  } | null;
  wageGroups?: RawWageGroup[] | null;
  taxesByType?: RawBreakdown[] | null;
  deductionsByCategory?: RawBreakdown[] | null;
  employerContributions?: RawBreakdown[] | { notSupplied?: boolean | null; items?: RawBreakdown[] | null } | null;
  netPayDetail?: { employeeReference?: string | null; employeeNumber?: string | null; netPay?: number | null; amount?: number | null }[] | null;
  unusedWageMappings?: { count?: number | null; note?: string | null } | number | null;
  limitations?: string[] | null;
}
export interface RawExportSummary {
  id: string; payrollRunId: string; kind: string; mode: string; targetFormat: string; status: string; checkDate: string; periodStart: string; periodEnd: string;
  accountingDate: string; journalReference: string; totalDebits: number; totalCredits: number; lineCount: number; checksum?: string | null;
  csvSchemaVersion: number; mappingVersionMax: number; reversalOfExportId?: string | null; replacesExportId?: string | null; reversedByExportId?: string | null;
  supersededByExportId?: string | null; reason?: string | null; generatedAtUtc: string; downloadCount: number; firstDownloadedAtUtc?: string | null;
  operationId?: string | null;
  options?: RawOptions | null;
}
export interface RawExportDetail extends RawExportSummary {
  lines?: RawJournalLine[] | null; stateHistory?: { fromStatus?: string | null; toStatus: string; reason?: string | null; occurredAtUtc: string }[] | null;
}
export interface RawPaged<T> { items?: T[] | null; page?: number; pageSize?: number; totalCount?: number }
export interface RawChartAccount {
  id: string; accountNumber: string; name: string; accountType: string; isActive: boolean; source?: string | null; externalReference?: string | null; mappingUseCount?: number | null;
}
export interface RawMapping {
  id: string; category: string; subcategory?: string | null; payrollCode?: string | null; employeeId?: string | null; departmentId?: string | null;
  locationCode?: string | null; groupDimension?: string | null; groupKey?: string | null; treatment: string; nonPostingReason?: string | null;
  chartAccountId?: string | null; accountNumber?: string | null;
  accountName?: string | null; accountType?: string | null; direction: string; effectiveFrom: string; effectiveTo?: string | null; mappingVersion: number;
  isActive: boolean; confirmedFundingModel?: boolean | null;
}
export interface RawAuditItem {
  id: string; occurredAtUtc: string; eventType: string; actorUserId?: string | null; actorType?: string | null; actorEmailSnapshot?: string | null;
  metadataJson?: string | null; correlationId?: string | null;
}

// ---- helpers ----

const str = (v: string | null | undefined): string | null => (v === undefined || v === null || v === '' ? null : v);

/** A money member that must be present: an unreadable value is kept as NaN so a screen can refuse to show a wrong total. */
const money = (v: number | null | undefined): number => toCents(v);

const NET_PAY_MODES: readonly NetPayDetailMode[] = ['Consolidated', 'PerEmployee'];
const WAGE_MODES: readonly WageMappingMode[] = ['SingleAccount', 'GroupBased', 'LegacyResolver'];
const OPTION_SOURCES: readonly OptionSource[] = ['Request', 'CompanySettings', 'LegacyDefault'];

/** An enum member the server sent, or null when it is absent or not one this UI knows (never a guess). */
function oneOf<T extends string>(value: string | null | undefined, allowed: readonly T[]): T | null {
  return allowed.find((a) => a === value) ?? null;
}
const dimensionOf = (v: string | null | undefined): WageGroupDimension | null => oneOf(v, WAGE_GROUP_DIMENSIONS);
const count = (v: number | null | undefined): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

export function mapAccountRef(raw: RawAccountRef | null | undefined): AccountRef | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string') return raw ? { accountNumber: raw, accountName: '' } : null;
  const accountNumber = raw.accountNumber ?? raw.number ?? '';
  const accountName = raw.accountName ?? raw.name ?? '';
  return accountNumber || accountName ? { accountNumber, accountName } : null;
}

/** An export with no `options` member was recorded before options existed: `recorded: false` and no values, which the screens label "Legacy / default". */
export function mapOptions(raw: RawOptions | null | undefined): ExportOptions {
  if (!raw) return { netPayDetailMode: null, wageMappingMode: null, wageGroupDimension: null, source: null, recorded: false, settingsVersion: null, groupConfigVersion: null };
  return {
    netPayDetailMode: oneOf(raw.netPayDetailMode, NET_PAY_MODES), wageMappingMode: oneOf(raw.wageMappingMode, WAGE_MODES), wageGroupDimension: dimensionOf(raw.wageGroupDimension),
    source: oneOf(raw.source, OPTION_SOURCES), recorded: raw.recorded !== false, settingsVersion: count(raw.settingsVersion), groupConfigVersion: count(raw.groupConfigVersion),
  };
}

// ---- responses ----

export function mapCapabilities(raw: Partial<AccountingExportCapabilities> | null | undefined): AccountingExportCapabilities {
  return {
    canPreview: raw?.canPreview === true,
    canGenerate: raw?.canGenerate === true,
    canViewPerEmployee: raw?.canViewPerEmployee === true,
    canManageMappings: raw?.canManageMappings === true,
    canManageChartOfAccounts: raw?.canManageChartOfAccounts === true,
    canReverse: raw?.canReverse === true,
    canViewAudit: raw?.canViewAudit === true,
  };
}

function mapEligible(raw: RawEligiblePayroll): EligiblePayroll {
  return {
    payrollRunId: raw.payrollRunId, payScheduleId: raw.payScheduleId ?? null, payScheduleLabel: raw.payScheduleLabel ?? 'Pay schedule not set',
    periodStart: raw.periodStart, periodEnd: raw.periodEnd, checkDate: raw.checkDate, employeeCount: raw.employeeCount,
    grossEarningsCents: money(raw.grossEarnings), netPayCents: money(raw.netPay),
    exportStatus: (raw.exportStatus ?? 'None') as EligiblePayroll['exportStatus'],
  };
}

export function mapEligiblePayrolls(raw: RawEligiblePayrolls | null | undefined): EligiblePayrolls {
  return {
    items: (raw?.items ?? []).map(mapEligible),
    payScheduleOptions: (raw?.payScheduleOptions ?? []).map((o) => ({ id: o.id ?? null, label: o.label })),
  };
}

function mapEmployee(raw: RawPreviewEmployee): PreviewEmployee {
  return {
    employeeId: raw.employeeId, employeeReference: str(raw.employeeReference), displayName: str(raw.displayName), department: str(raw.department),
    location: str(raw.location), grossEarningsCents: money(raw.grossEarnings), netPayCents: money(raw.netPay),
  };
}

export function mapJournalLine(raw: RawJournalLine): JournalLine {
  return {
    lineNumber: raw.lineNumber, side: raw.side === 'Credit' ? 'Credit' : 'Debit', amountCents: money(raw.amount), accountNumber: raw.accountNumber,
    accountName: raw.accountName, accountType: raw.accountType, category: raw.category, subcategory: str(raw.subcategory), payrollCode: str(raw.payrollCode),
    employeeReference: str(raw.employeeReference), department: str(raw.department), location: str(raw.location), classDimension: str(raw.classDimension),
    checkReference: str(raw.checkReference), description: raw.description, mappingId: raw.mappingId, mappingVersion: raw.mappingVersion,
    sourceLineIds: raw.sourceLineIds ?? [], groupDimension: dimensionOf(raw.groupDimension), groupKey: str(raw.groupKey), groupLabel: str(raw.groupLabel),
    resolutionLevel: str(raw.resolutionLevel), resolutionReason: str(raw.resolutionReason),
  };
}

function mapJournal(raw: RawJournal): Journal {
  return {
    reference: raw.reference, totalDebitsCents: money(raw.totalDebits), totalCreditsCents: money(raw.totalCredits), differenceCents: money(raw.difference),
    isBalanced: raw.isBalanced === true, lines: (raw.lines ?? []).map(mapJournalLine),
  };
}

function mapReconciliation(raw: RawReconciliation): ReconciliationFigures {
  return {
    finalizedGrossCents: money(raw.finalizedGross), employeeTaxesCents: money(raw.employeeTaxes), employerTaxesCents: money(raw.employerTaxes),
    preTaxDeductionsCents: money(raw.preTaxDeductions), postTaxDeductionsCents: money(raw.postTaxDeductions), garnishmentsCents: money(raw.garnishments),
    // null stays null: "not supplied by payroll" is never turned into a zero.
    employerContributionsCents: raw.employerContributions === null || raw.employerContributions === undefined ? null : money(raw.employerContributions),
    netPayCents: money(raw.netPay), totalPayrollCostCents: money(raw.totalPayrollCost), journalDebitsCents: money(raw.journalDebits),
    journalCreditsCents: money(raw.journalCredits), unmappedAmountCents: money(raw.unmappedAmount), excludedAmountCents: money(raw.excludedAmount),
    differenceCents: money(raw.difference), isComplete: raw.isComplete === true,
  };
}

function mapSourceLine(raw: RawSourceLine): SourceLine {
  return {
    id: raw.id, calculationId: raw.calculationId, employeeReference: str(raw.employeeReference), category: raw.category, subcategory: str(raw.subcategory),
    payrollCode: str(raw.payrollCode), jurisdiction: str(raw.jurisdiction), side: raw.side === 'Credit' ? 'Credit' : 'Debit', amountCents: money(raw.amount),
    provenance: raw.provenance === 'Correction' ? 'Correction' : 'Calculation', posted: raw.posted === true,
  };
}

export function mapUnmapped(raw: RawUnmapped): UnmappedItem {
  return {
    category: raw.category, subcategory: str(raw.subcategory), payrollCode: str(raw.payrollCode), lineCount: raw.lineCount, amountCents: money(raw.amount),
    reason: (raw.reason as UnmappedItem['reason']) ?? 'NoMapping',
  };
}

const mapExcluded = (raw: RawExcluded): ExcludedItem => ({ category: raw.category, amountCents: money(raw.amount), reason: raw.reason, mappingId: raw.mappingId });

/** `FederalIncomeTax` -> `Federal income tax`. */
const humanize = (v: string) => v.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());

function mapBreakdown(raw: RawBreakdown): BreakdownLine {
  const taxLabel = raw.subcategory ? humanize(`${raw.kind ? `${raw.kind} ` : ''}${raw.subcategory}`) : null;
  const label = raw.label ?? raw.name ?? raw.taxType ?? taxLabel ?? raw.taxTypeCode ?? raw.code ?? raw.category ?? raw.key ?? '';
  const key = raw.key ?? (raw.subcategory ? [raw.kind, raw.subcategory, raw.payrollCode].filter(Boolean).join(':') : null) ?? raw.taxTypeCode ?? raw.code ?? raw.category ?? label;
  // The deduction rows carry the account as two top-level members; the others as an `account` object.
  const account = mapAccountRef(raw.account) ?? (raw.accountNumber ? { accountNumber: raw.accountNumber, accountName: raw.accountName ?? '' } : null);
  return { key, label, amountCents: money(raw.amount ?? raw.total), account, note: str(raw.note) };
}

function mapEmployerContributions(raw: RawPreview['employerContributions']): EmployerContributions | null {
  if (raw === null || raw === undefined) return null;
  // An array lists one entry per category; an entry the payroll does not supply says `status: "NotSupplied"` (and has no amount). Only supplied entries are items.
  if (Array.isArray(raw)) {
    const supplied = raw.filter((r) => r.status !== 'NotSupplied' && r.amount !== null && r.amount !== undefined);
    return { notSupplied: supplied.length === 0, items: supplied.map(mapBreakdown) };
  }
  const items = (raw.items ?? []).map(mapBreakdown);
  return { notSupplied: raw.notSupplied === true || items.length === 0, items };
}

function mapWageGroup(raw: RawWageGroup): WageGroupLine {
  return {
    groupKey: raw.groupKey, label: raw.label ?? raw.groupKey, employeeCount: raw.employeeCount ?? 0, grossAmountCents: money(raw.grossAmount), account: mapAccountRef(raw.account),
    mappingId: str(raw.mappingId), mappingVersion: count(raw.mappingVersion), reason: str(raw.reason),
  };
}

function mapSummary(raw: NonNullable<RawPreview['summary']>): PreviewSummary {
  const optionalMoney = (v: number | null | undefined) => (v === null || v === undefined ? null : money(v));
  return {
    employeeCount: count(raw.employeeCount), groupCount: count(raw.groupCount), earningCodeCount: count(raw.earningCodeCount), deductionCodeCount: count(raw.deductionCodeCount),
    mappedComponents: count(raw.mappedComponents), missingMappings: count(raw.missingMappings), ambiguousMappings: count(raw.ambiguousMappings),
    totalDebitsCents: optionalMoney(raw.totalDebits), totalCreditsCents: optionalMoney(raw.totalCredits), varianceCents: optionalMoney(raw.variance),
  };
}

function mapConfiguration(raw: NonNullable<RawPreview['configuration']>): PreviewConfiguration {
  return { options: mapOptions(raw.options ?? raw), mappingVersionMax: count(raw.mappingVersionMax), exportKeyVersion: count(raw.exportKeyVersion) };
}

function mapNetPayDetail(raw: NonNullable<RawPreview['netPayDetail']>): NetPayDetailLine[] {
  return raw.map((d) => ({ employeeReference: d.employeeReference ?? d.employeeNumber ?? '', netPayCents: money(d.netPay ?? d.amount) }));
}

function mapUnusedWageMappings(raw: RawPreview['unusedWageMappings']): UnusedWageMappings | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') return { count: raw, note: null };
  return { count: raw.count ?? 0, note: str(raw.note) };
}

export function mapPreview(raw: RawPreview): ExportPreview {
  const existing: ExistingExportRef | null = raw.existingExport ? { id: raw.existingExport.id, status: raw.existingExport.status as ExportStatus } : null;
  return {
    payrollRunId: raw.payrollRunId, mode: raw.mode === 'PerEmployee' ? 'PerEmployee' : 'Compound', accountingDate: raw.accountingDate,
    status: raw.status === 'Ready' ? 'Ready' : 'ValidationFailed', currency: raw.currency ?? 'USD', employees: (raw.employees ?? []).map(mapEmployee),
    journal: raw.journal ? mapJournal(raw.journal) : null, reconciliation: raw.reconciliation ? mapReconciliation(raw.reconciliation) : null,
    sourceLines: (raw.sourceLines ?? []).map(mapSourceLine), unmapped: (raw.unmapped ?? []).map(mapUnmapped), excluded: (raw.excluded ?? []).map(mapExcluded),
    warnings: raw.warnings ?? [], blockers: raw.blockers ?? [], sourceCoverage: raw.sourceCoverage ?? [], existingExport: existing,
    options: mapOptions(raw.options ?? raw.configuration?.options), configuration: raw.configuration ? mapConfiguration(raw.configuration) : null,
    summary: raw.summary ? mapSummary(raw.summary) : null, wageGroups: (raw.wageGroups ?? []).map(mapWageGroup),
    taxesByType: (raw.taxesByType ?? []).map(mapBreakdown), deductionsByCategory: (raw.deductionsByCategory ?? []).map(mapBreakdown),
    employerContributions: mapEmployerContributions(raw.employerContributions),
    netPayDetail: raw.netPayDetail ? mapNetPayDetail(raw.netPayDetail) : null, unusedWageMappings: mapUnusedWageMappings(raw.unusedWageMappings),
    limitations: raw.limitations ?? [],
  };
}

export function mapExportSummary(raw: RawExportSummary): ExportSummary {
  return {
    id: raw.id, payrollRunId: raw.payrollRunId, kind: raw.kind as ExportSummary['kind'], mode: raw.mode === 'PerEmployee' ? 'PerEmployee' : 'Compound',
    targetFormat: raw.targetFormat, status: raw.status as ExportStatus, checkDate: raw.checkDate, periodStart: raw.periodStart, periodEnd: raw.periodEnd,
    accountingDate: raw.accountingDate, journalReference: raw.journalReference, totalDebitsCents: money(raw.totalDebits), totalCreditsCents: money(raw.totalCredits),
    lineCount: raw.lineCount, checksum: raw.checksum ?? '', csvSchemaVersion: raw.csvSchemaVersion, mappingVersionMax: raw.mappingVersionMax,
    reversalOfExportId: str(raw.reversalOfExportId), replacesExportId: str(raw.replacesExportId), reversedByExportId: str(raw.reversedByExportId),
    supersededByExportId: str(raw.supersededByExportId), reason: str(raw.reason), generatedAtUtc: raw.generatedAtUtc, downloadCount: raw.downloadCount,
    firstDownloadedAtUtc: str(raw.firstDownloadedAtUtc), operationId: raw.operationId ?? '', options: mapOptions(raw.options),
  };
}

export function mapExportDetail(raw: RawExportDetail): ExportDetail {
  const history: ExportStateChange[] = (raw.stateHistory ?? []).map((h) => ({
    fromStatus: h.fromStatus ?? null, toStatus: h.toStatus, reason: str(h.reason), occurredAtUtc: h.occurredAtUtc,
  }));
  return { ...mapExportSummary(raw), lines: (raw.lines ?? []).map(mapJournalLine), stateHistory: history };
}

/** POST create, reversal and replacement all answer `{ export, duplicate }`. */
export function mapCreateExportResult(raw: { export: RawExportDetail; duplicate?: boolean }): CreateExportResult {
  return { export: mapExportDetail(raw.export), duplicate: raw.duplicate === true };
}

/** Sync answers a bare export detail; reversal and replacement answer `{ export, duplicate }`. Both shapes are read, so the three share one mapper. */
export function mapExportResponse(raw: RawExportDetail | { export: RawExportDetail }): ExportDetail {
  return mapExportDetail('export' in raw && raw.export ? raw.export : (raw as RawExportDetail));
}

export function mapPaged<TRaw, T>(raw: RawPaged<TRaw> | null | undefined, map: (r: TRaw) => T): Paged<T> {
  const items = (raw?.items ?? []).map(map);
  return { items, page: raw?.page ?? 1, pageSize: raw?.pageSize ?? items.length, totalCount: raw?.totalCount ?? items.length };
}

export function mapChartAccount(raw: RawChartAccount): ChartAccount {
  return {
    id: raw.id, accountNumber: raw.accountNumber, name: raw.name, accountType: raw.accountType as ChartAccountType, isActive: raw.isActive === true,
    source: raw.source === 'CsvImport' ? 'CsvImport' : 'Manual', externalReference: str(raw.externalReference), mappingUseCount: raw.mappingUseCount ?? 0,
  };
}

export function mapMapping(raw: RawMapping): AccountingMapping {
  return {
    id: raw.id, category: raw.category, subcategory: str(raw.subcategory), payrollCode: str(raw.payrollCode), employeeId: str(raw.employeeId),
    departmentId: str(raw.departmentId), locationCode: str(raw.locationCode),
    ...(raw.groupDimension !== undefined ? { groupDimension: dimensionOf(raw.groupDimension) } : {}), ...(raw.groupKey !== undefined ? { groupKey: str(raw.groupKey) } : {}), treatment: raw.treatment === 'NonPosting' ? 'NonPosting' : 'Post',
    nonPostingReason: str(raw.nonPostingReason), chartAccountId: str(raw.chartAccountId), accountNumber: str(raw.accountNumber), accountName: str(raw.accountName),
    accountType: str(raw.accountType), direction: raw.direction === 'Credit' ? 'Credit' : 'Debit', effectiveFrom: raw.effectiveFrom, effectiveTo: str(raw.effectiveTo),
    mappingVersion: raw.mappingVersion, isActive: raw.isActive === true, confirmedFundingModel: raw.confirmedFundingModel === true,
  };
}

export function mapReadiness(raw: { ready?: boolean; missing?: RawUnmapped[] | null; ambiguous?: RawUnmapped[] | null } | null | undefined): MappingReadiness {
  return { ready: raw?.ready === true, missing: (raw?.missing ?? []).map(mapUnmapped), ambiguous: (raw?.ambiguous ?? []).map(mapUnmapped) };
}

export function mapImportResult(raw: Partial<ChartImportResult> | null | undefined): ChartImportResult {
  return { dryRun: raw?.dryRun === true, accepted: raw?.accepted ?? 0, rejected: raw?.rejected ?? 0, errors: (raw?.errors ?? []).map((e) => ({ row: e.row, message: e.message })) };
}

// ---- export settings, groups, wage groups, code mappings ----

export interface RawDefaultAccount { mappingId?: string | null; id?: string | null; accountNumber?: string | null; accountName?: string | null; number?: string | null; name?: string | null }
export interface RawExportSettings {
  netPayDetailMode?: string | null; wageMappingMode?: string | null; wageGroupDimension?: string | null; settingsVersion?: number | null; groupConfigVersion?: number | null;
  isConfigured?: boolean | null; updatedAtUtc?: string | null; updatedByUserId?: string | null;
  defaultWageAccount?: RawDefaultAccount | null; defaultClearingAccount?: RawDefaultAccount | null;
}

function mapDefaultAccount(raw: RawDefaultAccount | null | undefined): DefaultAccountRef | null {
  if (!raw) return null;
  const accountNumber = raw.accountNumber ?? raw.number ?? '';
  const accountName = raw.accountName ?? raw.name ?? '';
  return accountNumber || accountName ? { mappingId: str(raw.mappingId ?? raw.id), accountNumber, accountName } : null;
}

export function mapExportSettings(raw: RawExportSettings | null | undefined): ExportSettings {
  return {
    netPayDetailMode: oneOf(raw?.netPayDetailMode, NET_PAY_MODES) ?? 'Consolidated', wageMappingMode: oneOf(raw?.wageMappingMode, WAGE_MODES) ?? 'LegacyResolver',
    wageGroupDimension: dimensionOf(raw?.wageGroupDimension), settingsVersion: raw?.settingsVersion ?? 0, groupConfigVersion: raw?.groupConfigVersion ?? 0,
    isConfigured: raw?.isConfigured === true, updatedAtUtc: str(raw?.updatedAtUtc), updatedByUserId: str(raw?.updatedByUserId),
    defaultWageAccount: mapDefaultAccount(raw?.defaultWageAccount), defaultClearingAccount: mapDefaultAccount(raw?.defaultClearingAccount),
  };
}

export interface RawEmployeeGroup {
  id: string; name: string; description?: string | null; isActive?: boolean | null; effectiveFrom?: string | null; effectiveTo?: string | null; memberCount?: number | null;
  concurrencyVersion?: number | null;
}
export interface RawGroupMember {
  membershipId?: string | null; id?: string | null; employeeReference?: string | null; employeeNumber?: string | null; effectiveFrom: string; effectiveTo?: string | null;
}

export function mapEmployeeGroup(raw: RawEmployeeGroup): EmployeeGroup {
  return {
    id: raw.id, name: raw.name, description: str(raw.description), isActive: raw.isActive !== false, effectiveFrom: str(raw.effectiveFrom), effectiveTo: str(raw.effectiveTo),
    memberCount: raw.memberCount ?? 0, concurrencyVersion: raw.concurrencyVersion ?? 0,
  };
}

export function mapGroupMember(raw: RawGroupMember): EmployeeGroupMember {
  return {
    membershipId: raw.membershipId ?? raw.id ?? '', employeeReference: raw.employeeReference ?? raw.employeeNumber ?? '', effectiveFrom: raw.effectiveFrom, effectiveTo: str(raw.effectiveTo),
  };
}

export interface RawWageGroupGrid {
  dimension?: string | null; accountingDate?: string | null; groups?: RawWageGroup[] | null; employeesWithoutGroup?: number | null;
}

export function mapWageGroupGrid(raw: RawWageGroupGrid | null | undefined, requested: WageGroupDimension): WageGroupGrid {
  const groups: WageGroupRow[] = (raw?.groups ?? []).map((g) => {
    const account = mapAccountRef(g.account);
    const status: WageGroupStatus = oneOf<WageGroupStatus>(g.status, ['Mapped', 'Missing', 'Ambiguous']) ?? (account ? 'Mapped' : 'Missing');
    return { groupKey: g.groupKey, label: g.label ?? g.groupKey, employeeCount: g.employeeCount ?? 0, status, account, mappingId: str(g.mappingId), mappingVersion: count(g.mappingVersion) };
  });
  return { dimension: dimensionOf(raw?.dimension) ?? requested, accountingDate: str(raw?.accountingDate), groups, employeesWithoutGroup: raw?.employeesWithoutGroup ?? 0 };
}

export interface RawCodeItem {
  kind: string; codeId: string; code?: string | null; name: string; categoryCode?: string | null; classification?: string | null; taxTreatment?: string | null; source?: string | null;
  accountingCategory: string; payrollCode: string; mappingStatus?: string | null;
  mapping?: { id: string; accountNumber?: string | null; accountName?: string | null; effectiveFrom?: string | null; effectiveTo?: string | null; version?: number | null; mappingVersion?: number | null } | null;
  isActive?: boolean | null; employeesWithElection?: number | null; totalAmountInPayroll?: number | null; unavailableReason?: string | null;
}
export interface RawCodeReadiness extends RawPaged<RawCodeItem> {
  catalogs?: { deductions?: string | null; earnings?: string | null; benefits?: string | null } | null; appliesToJournal?: boolean | null; notice?: string | null;
}

const CODE_KINDS: readonly CodeKind[] = ['Deduction', 'Earning', 'Benefit'];
const availability = (v: string | null | undefined): 'Available' | 'Unavailable' => (v === 'Unavailable' ? 'Unavailable' : 'Available');

export function mapCodeItem(raw: RawCodeItem): CodeReadinessItem {
  return {
    kind: oneOf(raw.kind, CODE_KINDS) ?? 'Deduction', codeId: raw.codeId, code: str(raw.code), name: raw.name, categoryCode: str(raw.categoryCode), classification: str(raw.classification),
    taxTreatment: raw.taxTreatment === 'PreTax' || raw.taxTreatment === 'PostTax' ? raw.taxTreatment : null,
    source: raw.source === 'Employee' || raw.source === 'Employer' ? raw.source : null, accountingCategory: raw.accountingCategory, payrollCode: raw.payrollCode,
    mappingStatus: oneOf<CodeMappingStatus>(raw.mappingStatus, ['Mapped', 'Unmapped', 'Ambiguous']) ?? (raw.mapping ? 'Mapped' : 'Unmapped'),
    mapping: raw.mapping
      ? {
        id: raw.mapping.id, accountNumber: raw.mapping.accountNumber ?? '', accountName: raw.mapping.accountName ?? '', effectiveFrom: str(raw.mapping.effectiveFrom),
        effectiveTo: str(raw.mapping.effectiveTo), version: count(raw.mapping.version ?? raw.mapping.mappingVersion),
      }
      : null,
    isActive: raw.isActive !== false, employeesWithElection: count(raw.employeesWithElection),
    totalAmountInPayroll: raw.totalAmountInPayroll === null || raw.totalAmountInPayroll === undefined ? null : money(raw.totalAmountInPayroll), unavailableReason: str(raw.unavailableReason),
  };
}

export function mapCodeReadiness(raw: RawCodeReadiness | null | undefined, fallbackNotice: string): CodeReadiness {
  const paged = mapPaged(raw, mapCodeItem);
  return {
    ...paged, catalogs: { deductions: availability(raw?.catalogs?.deductions), earnings: availability(raw?.catalogs?.earnings), benefits: availability(raw?.catalogs?.benefits) },
    appliesToJournal: raw?.appliesToJournal === true, notice: raw?.notice || fallbackNotice,
  };
}

export interface RawBulkItem {
  kind: string; codeId: string; code?: string | null; name?: string | null; action?: string | null; existingMappingId?: string | null; existingAccount?: RawAccountRef | null; reason?: string | null;
  existingAccountNumber?: string | null; existingAccountName?: string | null;
}
export interface RawBulkPreview {
  items?: RawBulkItem[] | null; create?: number | null; replace?: number | null; skip?: number | null; conflict?: number | null;
  totals?: { create?: number | null; replace?: number | null; skip?: number | null; conflict?: number | null } | null; errors?: (string | { message?: string | null })[] | null;
}

const BULK_ACTIONS: readonly CodeBulkAction[] = ['Create', 'Replace', 'Skip', 'Conflict'];
const normalizeAction = (v: string | null | undefined): CodeBulkAction => BULK_ACTIONS.find((a) => a.toLowerCase() === (v ?? '').toLowerCase()) ?? 'Skip';

export function mapBulkPreview(raw: RawBulkPreview | null | undefined): CodeBulkPreview {
  const items: CodeBulkPreviewItem[] = (raw?.items ?? []).map((i) => ({
    kind: oneOf(i.kind, CODE_KINDS) ?? 'Deduction', codeId: i.codeId, code: str(i.code), name: str(i.name), action: normalizeAction(i.action), existingMappingId: str(i.existingMappingId),
    existingAccount: mapAccountRef(i.existingAccount) ?? (i.existingAccountNumber ? { accountNumber: i.existingAccountNumber, accountName: i.existingAccountName ?? '' } : null), reason: str(i.reason),
  }));
  const tally = (a: CodeBulkAction, reported: number | null | undefined) => reported ?? items.filter((i) => i.action === a).length;
  return {
    items, create: tally('Create', raw?.totals?.create ?? raw?.create), replace: tally('Replace', raw?.totals?.replace ?? raw?.replace), skip: tally('Skip', raw?.totals?.skip ?? raw?.skip),
    conflict: tally('Conflict', raw?.totals?.conflict ?? raw?.conflict), errors: (raw?.errors ?? []).map((e) => (typeof e === 'string' ? e : (e.message ?? ''))).filter(Boolean),
  };
}

export function mapBulkResult(raw: { created?: number | null; replaced?: number | null; skipped?: number | null; conflicts?: number | null; replayed?: boolean | null } | null | undefined): CodeBulkResult {
  return { created: raw?.created ?? 0, replaced: raw?.replaced ?? 0, skipped: raw?.skipped ?? 0, conflicts: raw?.conflicts ?? 0, replayed: raw?.replayed === true };
}

/** The audit endpoint answers the module's audit page `{ items, page, pageSize, totalCount }`; a bare list is read too. */
export function mapAudit(raw: RawPaged<RawAuditItem> | RawAuditItem[] | null | undefined): ExportAuditItem[] {
  const items = Array.isArray(raw) ? raw : (raw?.items ?? []);
  return items.map((e) => ({
    id: e.id, occurredAtUtc: e.occurredAtUtc, actorUserId: str(e.actorUserId), actorType: str(e.actorType), actorEmailSnapshot: str(e.actorEmailSnapshot), eventType: e.eventType,
    metadataJson: str(e.metadataJson),
  }));
}

/** Reads the validation report out of a 422 problem body: `extensions.report` (the real shape), or a flattened `report` member. */
export function reportFromProblem(details: Record<string, unknown> | null | undefined): RawPreview | null {
  if (!details) return null;
  const extensions = details.extensions;
  if (extensions && typeof extensions === 'object' && 'report' in extensions) return (extensions as { report: RawPreview }).report ?? null;
  if ('report' in details && details.report && typeof details.report === 'object') return details.report as RawPreview;
  return null;
}

// ---- requests ----

function query(entries: [string, string | number | boolean | undefined | null][]): string {
  const q = new URLSearchParams();
  for (const [k, v] of entries) if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export const eligiblePayrollsQuery = (p?: EligiblePayrollParams) => query([['payScheduleId', p?.payScheduleId], ['from', p?.from], ['to', p?.to]]);
/** The option fields are sent only when a person chose them: omitted means "use the company setting" (or the original behavior when none is set). */
const optionEntries = (p: ExportOptionsInput): [string, string | undefined][] => [
  ['netPayDetailMode', p.netPayDetailMode], ['wageMappingMode', p.wageMappingMode], ['wageGroupDimension', p.wageMappingMode === 'GroupBased' ? p.wageGroupDimension : undefined],
];
export const previewQuery = (p: PreviewParams) => query([['payrollRunId', p.payrollRunId], ['mode', p.mode], ['accountingDate', p.accountingDate], ...optionEntries(p)]);
export const listExportsQuery = (p?: ListExportsParams) =>
  query([['runId', p?.runId], ['status', p?.status], ['kind', p?.kind], ['page', p?.page], ['pageSize', p?.pageSize]]);
export const chartQuery = (p?: ListChartAccountsParams) =>
  query([['search', p?.search], ['type', p?.type], ['active', p?.active], ['page', p?.page], ['pageSize', p?.pageSize]]);
export const mappingsQuery = (p?: ListMappingsParams) => query([['category', p?.category], ['activeOnly', p?.activeOnly], ['page', p?.page], ['pageSize', p?.pageSize]]);

/** The company is never sent: the server takes it from the session. The target format is always Csv in this release. */
export function createExportBody(input: CreateExportInput): Record<string, unknown> {
  return {
    payrollRunId: input.payrollRunId, mode: input.mode, ...(input.accountingDate ? { accountingDate: input.accountingDate } : {}), targetFormat: 'Csv',
    ...Object.fromEntries(optionEntries(input).filter(([, v]) => v !== undefined)),
  };
}

export const reversalBody = (reason: string) => ({ reason: reason.trim() });
export const replacementBody = (input: ReplacementInput) => ({ reason: input.reason.trim(), ...(input.accountingDate ? { accountingDate: input.accountingDate } : {}) });

export function createAccountBody(input: CreateChartAccountInput): Record<string, unknown> {
  return {
    accountNumber: input.accountNumber.trim(), name: input.name.trim(), accountType: input.accountType,
    ...(input.externalReference?.trim() ? { externalReference: input.externalReference.trim() } : {}),
  };
}

export function createMappingBody(input: CreateMappingInput): Record<string, unknown> {
  const opt = (k: string, v: string | undefined) => (v && v.trim() ? { [k]: v.trim() } : {});
  return {
    category: input.category, direction: categorySide(input.category) ?? undefined, ...opt('subcategory', input.subcategory), ...opt('payrollCode', input.payrollCode), ...opt('employeeId', input.employeeId),
    ...opt('departmentId', input.departmentId), ...opt('locationCode', input.locationCode),
    ...(input.groupDimension && input.groupKey?.trim() ? { groupDimension: input.groupDimension, groupKey: input.groupKey.trim() } : {}), treatment: input.treatment,
    ...(input.treatment === 'NonPosting' ? opt('nonPostingReason', input.nonPostingReason) : opt('chartAccountId', input.chartAccountId)),
    effectiveFrom: input.effectiveFrom, ...(input.confirmedFundingModel ? { confirmedFundingModel: true } : {}),
  };
}

export const wageGroupsQuery = (p: WageGroupParams) => query([['payrollRunId', p.payrollRunId], ['dimension', p.dimension], ['accountingDate', p.accountingDate]]);
export const codeReadinessQuery = (p?: CodeReadinessParams) =>
  query([['kind', p?.kind], ['search', p?.search?.trim()], ['payrollRunId', p?.payrollRunId], ['page', p?.page], ['pageSize', p?.pageSize]]);

export const saveSettingsBody = (input: SaveExportSettingsInput) => ({
  netPayDetailMode: input.netPayDetailMode, wageMappingMode: input.wageMappingMode,
  ...(input.wageMappingMode === 'GroupBased' && input.wageGroupDimension ? { wageGroupDimension: input.wageGroupDimension } : {}), expectedVersion: input.expectedVersion,
});

export const createGroupBody = (input: CreateEmployeeGroupInput) => ({
  name: input.name.trim(), ...(input.description?.trim() ? { description: input.description.trim() } : {}), ...(input.effectiveFrom ? { effectiveFrom: input.effectiveFrom } : {}),
});
export const updateGroupBody = (input: UpdateEmployeeGroupInput) => ({
  name: input.name.trim(), description: input.description?.trim() || null, effectiveTo: input.effectiveTo || null, expectedVersion: input.expectedVersion,
});
export const addMembersBody = (input: AddGroupMembersInput) => ({ employeeIds: input.employeeIds, effectiveFrom: input.effectiveFrom, ...(input.effectiveTo ? { effectiveTo: input.effectiveTo } : {}) });

const bulkCodes = (r: CodeBulkRequest) => r.codes.map((c) => ({ kind: c.kind, codeId: c.codeId }));
export const bulkPreviewBody = (r: CodeBulkRequest) => ({ codes: bulkCodes(r), chartAccountId: r.chartAccountId, effectiveFrom: r.effectiveFrom, replaceExisting: r.replaceExisting });
/** `confirm: true` is added here and only here, so the apply call can never be made without it. */
export const bulkApplyBody = (r: CodeBulkApplyInput) => ({ ...bulkPreviewBody(r), confirm: true, idempotencyKey: r.idempotencyKey });
